import { type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, BarChart3, BookOpen, CheckCircle, DollarSign, GraduationCap, Minus, TrendingDown, TrendingUp, Users } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { analyticsService, type BehaviorAnalysisEntry, type ClassPerformance, type RevenueAnalysis, type SubjectPerformance } from '../services/analyticsService'
import { ClientRegister, StatusDot, type RegisterColumn } from '../components/register/ClientRegister'
import {
  REVENUE_SOURCES,
  behaviourConfig,
  change,
  classConfig,
  monthLabel,
  num,
  revenueConfig,
  subjectConfig,
} from '../components/analytics/analyticsRegisters'
import { formatSchoolDate } from '../components/students/studentDisplay'

// Chart ink and the checked categorical order (blue, orange, aqua, yellow, magenta, green).
// A single series uses the signed-in role's own colour, so it matches the sidebar.
const INK = { grid: '#e1e0d9', axis: '#898781', text: '#52514e' }
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300']
const THEME = 'rgb(var(--primary-600))'

const TABS = [
  { id: 'overview', name: 'Overview', icon: BarChart3 },
  { id: 'academic', name: 'Academic', icon: BookOpen },
  { id: 'attendance', name: 'Attendance', icon: CheckCircle },
  { id: 'financial', name: 'Money received', icon: DollarSign },
  { id: 'behavior', name: 'Behaviour', icon: AlertTriangle },
] as const
type Tab = (typeof TABS)[number]['id']

const RANGES = [3, 6, 12, 24]

const pct = (value: unknown) => (num(value) === null ? '—' : `${num(value)!.toFixed(1)}%`)
const ugx = (value: unknown) => `UGX ${Number(value || 0).toLocaleString()}`
const compact = (value: number) => (value >= 1_000_000 ? `${(value / 1_000_000).toFixed(1)}M` : value >= 1_000 ? `${Math.round(value / 1_000)}k` : String(value))

const axis = { stroke: INK.grid, tick: { fill: INK.axis, fontSize: 12 }, tickLine: false }
const tooltipStyle = { borderRadius: 8, border: '1px solid rgba(11,11,11,0.10)', fontSize: 12 }

function Panel({ title, description, children, className = '' }: { title: string; description?: string; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      <div className="card-titlebar">
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
        </div>
      </div>
      <div className="px-6 py-4">{children}</div>
    </section>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return <div className="flex h-56 items-center justify-center rounded-lg border border-dashed border-gray-200 px-6 text-center text-sm text-gray-500">{children}</div>
}

function Loading() {
  return <div className="h-56 animate-pulse rounded-lg bg-gray-100" />
}

/** A headline figure with its real change from the span before, or none when there was nothing to compare. */
function Tile({
  label,
  value,
  note,
  delta,
  goodWhenUp = true,
  icon,
}: {
  label: string
  value: string
  note: string
  delta: ReturnType<typeof change>
  goodWhenUp?: boolean
  icon: ReactNode
}) {
  const good = delta ? (goodWhenUp ? delta.difference > 0 : delta.difference < 0) : false
  const flat = delta ? delta.difference === 0 : true
  const Icon = !delta || flat ? Minus : delta.difference > 0 ? TrendingUp : TrendingDown
  return (
    <div className="card">
      <div className="flex items-start gap-4 px-5 py-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">{icon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-500">{label}</p>
          <p className="mt-0.5 text-2xl font-bold tabular-nums text-gray-900">{value}</p>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs">
            {delta ? (
              <span className={`inline-flex items-center gap-1 font-semibold ${flat ? 'text-gray-500' : good ? 'text-green-700' : 'text-red-700'}`}>
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {delta.text}
                <span className="sr-only">{flat ? 'no change' : good ? 'better' : 'worse'} than before</span>
              </span>
            ) : (
              <span className="text-gray-400">No earlier figure</span>
            )}
            <span className="text-gray-400">{note}</span>
          </p>
        </div>
      </div>
    </div>
  )
}

const classColumns: RegisterColumn<ClassPerformance>[] = [
  { header: 'Class', cell: (row) => <span className="font-medium text-gray-900">{row.class_name}</span>, exportValue: (row) => row.class_name },
  { header: 'Learners', cell: (row) => row.total_students, exportValue: (row) => row.total_students, align: 'right' },
  { header: 'Results', cell: (row) => row.results, exportValue: (row) => row.results, align: 'right' },
  { header: 'Average', cell: (row) => <span className={(num(row.average_score) ?? 100) < 50 ? 'font-semibold text-red-700' : 'font-semibold text-gray-900'}>{pct(row.average_score)}</span>, exportValue: (row) => pct(row.average_score), align: 'right' },
  { header: 'Pass rate', cell: (row) => pct(row.pass_rate), exportValue: (row) => pct(row.pass_rate), align: 'right' },
  { header: 'Attendance', cell: (row) => pct(row.attendance_rate), exportValue: (row) => pct(row.attendance_rate), align: 'right' },
]

const subjectColumns: RegisterColumn<SubjectPerformance>[] = [
  { header: 'Subject', cell: (row) => <span className="font-medium text-gray-900">{row.subject_name}</span>, exportValue: (row) => row.subject_name },
  { header: 'Learners', cell: (row) => row.total_students, exportValue: (row) => row.total_students, align: 'right' },
  { header: 'Average', cell: (row) => <span className={Number(row.average_score) < 50 ? 'font-semibold text-red-700' : 'font-semibold text-gray-900'}>{pct(row.average_score)}</span>, exportValue: (row) => pct(row.average_score), align: 'right' },
  {
    header: 'Before',
    cell: (row) => {
      const delta = change(row.average_score, row.previous_average_score, 'points')
      return delta ? <span className={delta.difference >= 0 ? 'text-green-700' : 'text-red-700'}>{delta.text}</span> : <span className="text-gray-400">—</span>
    },
    exportValue: (row) => change(row.average_score, row.previous_average_score, 'points')?.text ?? '',
    align: 'right',
  },
  { header: 'Pass rate', cell: (row) => pct(row.pass_rate), exportValue: (row) => pct(row.pass_rate), align: 'right' },
  ...(['A', 'B', 'C', 'D', 'F'] as const).map((grade) => ({
    header: grade,
    cell: (row: SubjectPerformance) => row.grade_distribution[grade] || <span className="text-gray-300">0</span>,
    exportValue: (row: SubjectPerformance) => row.grade_distribution[grade] ?? 0,
    align: 'right' as const,
  })),
]

const revenueColumns: RegisterColumn<RevenueAnalysis>[] = [
  { header: 'Month', cell: (row) => <span className="font-medium text-gray-900">{monthLabel(row.period)}</span>, exportValue: (row) => row.period },
  ...REVENUE_SOURCES.map((source) => ({
    header: source.label,
    cell: (row: RevenueAnalysis) => (Number(row[source.key]) ? Number(row[source.key]).toLocaleString() : <span className="text-gray-300">—</span>),
    exportValue: (row: RevenueAnalysis) => Number(row[source.key] || 0),
    align: 'right' as const,
  })),
  { header: 'Total (UGX)', cell: (row) => <span className="font-semibold text-gray-900">{Number(row.total).toLocaleString()}</span>, exportValue: (row) => Number(row.total), align: 'right' },
]

const TRENDS: Record<string, { label: string; dot: string }> = {
  improving: { label: 'Fewer than before', dot: 'bg-emerald-500' },
  declining: { label: 'More than before', dot: 'bg-red-500' },
  stable: { label: 'Same as before', dot: 'bg-gray-400' },
}

const behaviourColumns: RegisterColumn<BehaviorAnalysisEntry>[] = [
  { header: 'Category', cell: (row) => <span className="font-medium text-gray-900">{row.incident_type}</span>, exportValue: (row) => row.incident_type },
  { header: 'Incidents', cell: (row) => row.count, exportValue: (row) => row.count, align: 'right' },
  { header: 'Share', cell: (row) => `${Number(row.percentage).toFixed(1)}%`, exportValue: (row) => `${row.percentage}%`, align: 'right' },
  { header: 'Against the span before', cell: (row) => <StatusDot {...(TRENDS[row.trend] ?? TRENDS.stable)} />, exportValue: (row) => (TRENDS[row.trend] ?? TRENDS.stable).label },
]

export default function Analytics() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const tab: Tab = TABS.some((entry) => entry.id === requestedTab) ? (requestedTab as Tab) : 'overview'
  const months = RANGES.includes(Number(searchParams.get('months'))) ? Number(searchParams.get('months')) : 6
  const setParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    params.set(key, value)
    setSearchParams(params, { replace: true })
  }

  const summary = useQuery({ queryKey: ['analytics-summary', months], queryFn: () => analyticsService.getSummary(months) })
  const trends = useQuery({ queryKey: ['analytics-trends', months], queryFn: () => analyticsService.getPerformanceTrends(months) })
  const classes = useQuery({ queryKey: ['analytics-classes', months], queryFn: () => analyticsService.getClassPerformance(months), enabled: tab === 'overview' || tab === 'academic' })
  const subjects = useQuery({ queryKey: ['analytics-subjects', months], queryFn: () => analyticsService.getSubjectPerformance(months), enabled: tab === 'academic' })
  const revenue = useQuery({ queryKey: ['analytics-revenue', months], queryFn: () => analyticsService.getRevenueAnalysis(months), enabled: tab === 'financial' })
  const attendance = useQuery({ queryKey: ['analytics-attendance', months], queryFn: () => analyticsService.getAttendancePatterns(months), enabled: tab === 'attendance' })
  const behaviour = useQuery({ queryKey: ['analytics-behaviour', months], queryFn: () => analyticsService.getBehaviorAnalysis(months), enabled: tab === 'behavior' })

  const s = summary.data
  const span = s ? `${formatSchoolDate(s.window.start)} – ${formatSchoolDate(s.window.end)}` : ''
  const before = s ? `vs ${formatSchoolDate(s.window.previous_start)} – ${formatSchoolDate(s.window.previous_end)}` : ''
  const trendRows = (trends.data ?? []).map((row) => ({
    ...row,
    month: monthLabel(row.period),
    exam: num(row.academic_performance),
    attendance: num(row.attendance_rate),
    money: Number(row.money_received || 0),
  }))
  const hasScores = trendRows.some((row) => row.exam !== null || row.attendance !== null)
  const hasMoney = trendRows.some((row) => row.money > 0)
  const classChart = (classes.data ?? []).filter((row) => num(row.average_score) !== null).map((row) => ({ name: row.class_name, average: num(row.average_score) }))
  const subjectChart = [...(subjects.data ?? [])].sort((a, b) => Number(b.average_score) - Number(a.average_score)).map((row) => ({ name: row.subject_name, average: Number(row.average_score) }))
  const revenueChart = (revenue.data ?? []).map((row) => ({ month: monthLabel(row.period), ...Object.fromEntries(REVENUE_SOURCES.map((source) => [source.key, Number(row[source.key] || 0)])) }))
  const activeSources = REVENUE_SOURCES.filter((source) => (revenue.data ?? []).some((row) => Number(row[source.key] || 0) > 0))

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">How the school is doing, from its own exam results, registers, receipts and incident reports.</p>
        </div>
        <div className="text-right">
          <div role="group" aria-label="Months covered" className="segmented">
            {RANGES.map((range) => (
              <button key={range} type="button" aria-pressed={months === range} onClick={() => setParam('months', String(range))} className="segmented-option px-3 py-1.5 text-sm">
                {range} months
              </button>
            ))}
          </div>
          {span && <p className="mt-1.5 text-xs text-gray-500">{span}, {before}</p>}
        </div>
      </div>

      {summary.isError && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">The analytics could not be loaded. Refresh to try again.</div>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          label="Exam average"
          value={pct(s?.average_performance)}
          note={s ? `${s.results_recorded.toLocaleString()} results · ${pct(s.pass_rate)} passed` : ''}
          delta={change(s?.average_performance, s?.previous_average_performance, 'points')}
          icon={<GraduationCap className="h-5 w-5" />}
        />
        <Tile
          label="Attendance"
          value={pct(s?.average_attendance)}
          note={s ? `${s.attendance_records.toLocaleString()} register marks` : ''}
          delta={change(s?.average_attendance, s?.previous_average_attendance, 'points')}
          icon={<CheckCircle className="h-5 w-5" />}
        />
        <Tile
          label="Money received"
          value={ugx(s?.money_received)}
          note={s ? `${s.collection_rate === null ? 'No invoices yet' : `${pct(s.collection_rate)} of billed fees collected`}` : ''}
          delta={change(s?.money_received, s?.previous_money_received, 'percent')}
          icon={<DollarSign className="h-5 w-5" />}
        />
        <Tile
          label="Behaviour incidents"
          value={(s?.behavior_incidents ?? 0).toLocaleString()}
          note={s ? `${s.total_students} learners · ${s.total_classes} classes` : ''}
          delta={change(s?.behavior_incidents, s?.previous_behavior_incidents, 'count')}
          goodWhenUp={false}
          icon={<Users className="h-5 w-5" />}
        />
      </div>

      <nav aria-label="Analytics" className="flex flex-wrap gap-x-1 border-b border-gray-200">
        {TABS.map(({ id, name, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setParam('tab', id)}
            aria-current={tab === id ? 'page' : undefined}
            className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === id ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'}`}
          >
            <Icon className="h-4 w-4" />
            {name}
          </button>
        ))}
      </nav>

      {tab === 'overview' && (
        <div className="grid gap-6 xl:grid-cols-2">
          <Panel title="Exam average and attendance, by month" description="Both in percent. A gap is a month with nothing recorded.">
            {trends.isLoading ? <Loading /> : hasScores ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trendRows} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={INK.grid} />
                  <XAxis dataKey="month" {...axis} />
                  <YAxis domain={[0, 100]} unit="%" {...axis} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => `${Number(value).toFixed(1)}%`} />
                  <Legend iconType="plainline" wrapperStyle={{ fontSize: 12, color: INK.text }} />
                  <Line type="monotone" dataKey="exam" name="Exam average" stroke={SERIES[0]} strokeWidth={2} dot={{ r: 4 }} connectNulls={false} />
                  <Line type="monotone" dataKey="attendance" name="Attendance" stroke={SERIES[1]} strokeWidth={2} dot={{ r: 4 }} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <Empty>No exam results or register marks in these months.</Empty>}
          </Panel>
          <Panel title="Money received, by month" description="Receipts, less refunds, in UGX.">
            {trends.isLoading ? <Loading /> : hasMoney ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={trendRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={INK.grid} />
                  <XAxis dataKey="month" {...axis} />
                  <YAxis tickFormatter={compact} {...axis} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(15,31,61,0.05)' }} formatter={(value: number) => ugx(value)} />
                  <Bar dataKey="money" name="Money received" fill={THEME} radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty>No receipts recorded in these months.</Empty>}
          </Panel>
          <Panel className="xl:col-span-2" title="Class averages" description="Exam average for each class that has results in these months.">
            {classes.isLoading ? <Loading /> : classChart.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={classChart} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={INK.grid} />
                  <XAxis dataKey="name" {...axis} interval={0} />
                  <YAxis domain={[0, 100]} unit="%" {...axis} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(15,31,61,0.05)' }} formatter={(value: number) => `${Number(value).toFixed(1)}%`} />
                  <Bar dataKey="average" name="Exam average" fill={THEME} radius={[4, 4, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty>No class has exam results in these months.</Empty>}
          </Panel>
        </div>
      )}

      {tab === 'academic' && (
        <div className="space-y-6">
          <Panel title="Subject averages" description="Every subject examined in these months, strongest first.">
            {subjects.isLoading ? <Loading /> : subjectChart.length ? (
              <ResponsiveContainer width="100%" height={Math.max(220, subjectChart.length * 26)}>
                <BarChart data={subjectChart} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid horizontal={false} stroke={INK.grid} />
                  <XAxis type="number" domain={[0, 100]} unit="%" {...axis} />
                  <YAxis type="category" dataKey="name" width={150} {...axis} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(15,31,61,0.05)' }} formatter={(value: number) => `${Number(value).toFixed(1)}%`} />
                  <Bar dataKey="average" name="Exam average" fill={THEME} radius={[0, 4, 4, 0]} maxBarSize={16} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty>No subject has exam results in these months.</Empty>}
          </Panel>
          <ClientRegister
            title="Subjects"
            description="Averages, pass rates and grades. “Before” compares with the same length of time before."
            rows={subjects.data}
            config={subjectConfig}
            columns={subjectColumns}
            rowKey={(row) => row.subject_name}
            noun={['subject', 'subjects']}
            exportName={{ file: `subject-performance-${months}-months`, title: `Subject performance, last ${months} months` }}
            isLoading={subjects.isLoading}
            isError={subjects.isError}
            onRetry={() => void subjects.refetch()}
            emptyText="No subject has exam results in these months."
          />
          <ClientRegister
            title="Classes"
            description="Every class: learners enrolled, results, average, pass rate and attendance."
            rows={classes.data}
            config={classConfig}
            columns={classColumns}
            rowKey={(row) => row.class_id}
            noun={['class', 'classes']}
            exportName={{ file: `class-performance-${months}-months`, title: `Class performance, last ${months} months` }}
            isLoading={classes.isLoading}
            isError={classes.isError}
            onRetry={() => void classes.refetch()}
            emptyText="No classes set up yet."
          />
        </div>
      )}

      {tab === 'attendance' && (
        <div className="grid gap-6 xl:grid-cols-5">
          <Panel className="xl:col-span-3" title="Attendance by day of the week" description="Share of register marks that were present, late or half day.">
            {attendance.isLoading ? <Loading /> : attendance.data?.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={attendance.data.map((row) => ({ ...row, rate: Number(row.attendance_rate) }))} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={INK.grid} />
                  <XAxis dataKey="day_of_week" {...axis} />
                  <YAxis domain={[0, 100]} unit="%" {...axis} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(15,31,61,0.05)' }} formatter={(value: number) => `${Number(value).toFixed(1)}%`} />
                  <Bar dataKey="rate" name="Attendance" fill={THEME} radius={[4, 4, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty>No register marks in these months.</Empty>}
          </Panel>
          <Panel className="xl:col-span-2" title="By weekday" description="The counts behind the chart.">
            {attendance.data?.length ? (
              <div className="sheet-wrap">
                <table className="sheet">
                  <thead><tr><th>Day</th><th className="text-right">Days</th><th className="text-right">Present</th><th className="text-right">Absent</th><th className="text-right">Rate</th></tr></thead>
                  <tbody>
                    {attendance.data.map((row) => (
                      <tr key={row.day_of_week}>
                        <td className="font-medium text-gray-900">{row.day_of_week}</td>
                        <td className="text-right tabular-nums">{row.total_days}</td>
                        <td className="text-right tabular-nums">{row.days_present}</td>
                        <td className="text-right tabular-nums">{row.days_absent}</td>
                        <td className="text-right font-semibold tabular-nums">{pct(row.attendance_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty>Nothing to count yet.</Empty>}
          </Panel>
          <div className="xl:col-span-5">
            <ClientRegister
              title="Attendance by class"
              description="Rate for each class over these months; classes with no register marks show a dash."
              rows={classes.data}
              config={classConfig}
              columns={classColumns.filter((column) => ['Class', 'Learners', 'Attendance'].includes(column.header))}
              rowKey={(row) => row.class_id}
              noun={['class', 'classes']}
              exportName={{ file: `attendance-by-class-${months}-months`, title: `Attendance by class, last ${months} months` }}
              isLoading={classes.isLoading}
              emptyText="No classes set up yet."
            />
          </div>
        </div>
      )}

      {tab === 'financial' && (
        <div className="space-y-6">
          <Panel title="Money received, by what it was for" description="Receipts less refunds, each month, in UGX.">
            {revenue.isLoading ? <Loading /> : activeSources.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueChart} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={INK.grid} />
                  <XAxis dataKey="month" {...axis} />
                  <YAxis tickFormatter={compact} {...axis} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(15,31,61,0.05)' }} formatter={(value: number) => ugx(value)} />
                  <Legend iconType="square" wrapperStyle={{ fontSize: 12, color: INK.text }} />
                  {/* Colour follows the source, never its rank, so a month with no transport does not repaint the rest. */}
                  {REVENUE_SOURCES.map((source, index) =>
                    activeSources.includes(source) ? (
                      <Bar key={source.key} dataKey={source.key} name={source.label} stackId="money" fill={SERIES[index]} stroke="#fff" strokeWidth={2} maxBarSize={40} />
                    ) : null,
                  )}
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty>No receipts recorded in these months.</Empty>}
          </Panel>
          <ClientRegister
            title="By month"
            description="The figures behind the chart."
            rows={revenue.data}
            config={revenueConfig}
            columns={revenueColumns}
            rowKey={(row) => row.period}
            noun={['month', 'months']}
            exportName={{ file: `money-received-${months}-months`, title: `Money received, last ${months} months` }}
            isLoading={revenue.isLoading}
            isError={revenue.isError}
            onRetry={() => void revenue.refetch()}
            emptyText="No months to show."
          />
        </div>
      )}

      {tab === 'behavior' && (
        <div className="space-y-6">
          <Panel title="Incidents by category" description="Recorded in these months, most common first.">
            {behaviour.isLoading ? <Loading /> : behaviour.data?.length ? (
              <ResponsiveContainer width="100%" height={Math.max(200, behaviour.data.length * 30)}>
                <BarChart data={behaviour.data} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid horizontal={false} stroke={INK.grid} />
                  <XAxis type="number" allowDecimals={false} {...axis} />
                  <YAxis type="category" dataKey="incident_type" width={150} {...axis} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(15,31,61,0.05)' }} />
                  <Bar dataKey="count" name="Incidents" fill={THEME} radius={[0, 4, 4, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty>No behaviour incidents recorded in these months.</Empty>}
          </Panel>
          <ClientRegister
            title="Categories"
            description="Each category against the same length of time before."
            rows={behaviour.data}
            config={behaviourConfig}
            columns={behaviourColumns}
            rowKey={(row) => row.incident_type}
            noun={['category', 'categories']}
            exportName={{ file: `behaviour-${months}-months`, title: `Behaviour incidents, last ${months} months` }}
            isLoading={behaviour.isLoading}
            isError={behaviour.isError}
            onRetry={() => void behaviour.refetch()}
            emptyText="No behaviour incidents recorded in these months."
          />
        </div>
      )}
    </div>
  )
}
